import {DEFAULT_ERROR_MESSAGES} from '@/errors/equalsErrorMappings.js';
import {resolveEqualsErrorMessage} from '@/errors/equalsErrorMappings.js';
import {resolveEqualsCardsErrorMessage} from '@/errors/equalsCardsErrorMappings.js';
import {resolveEquals3dsErrorMessage} from '@/errors/equals3dsErrorMappings.js';
import {
  isEqualsTransferApi,
  resolveEqualsTransferErrorMessage,
} from '@/errors/equalsTransferErrorMappings.js';
import {
  isCfsActionApi,
  resolveCfsActionErrorMessage,
} from '@/errors/cfsActionErrorMappings.js';
import {
  isCounterpartyApi,
  resolveCounterpartyErrorMessage,
} from '@/errors/counterpartyErrorMappings.js';
import {
  isPaywallApi,
  resolvePaywallErrorMessage,
} from '@/errors/paywallErrorMappings.js';
import {
  isInvoiceApi,
  resolveInvoiceErrorMessage,
} from '@/errors/invoiceErrorMappings.js';

/**
 * Central registry for mapping backend errors -> user-facing localized messages.
 *
 * Add new domains by appending a resolver entry here.
 * This keeps `callAPI()` clean and avoids 23+ inline conditions.
 */
const RESOLVERS = [
  {
    match: (api) => isEqualsTransferApi(api),
    resolve: ({api, error, locale}) =>
      resolveEqualsTransferErrorMessage({api, error, locale}),
  },
  {
    match: (api) =>
      typeof api === 'string' && api.includes('equals/3ds-validation'),
    resolve: ({api, error, locale}) =>
      resolveEquals3dsErrorMessage({api, error, locale}),
  },
  {
    match: (api) =>
      typeof api === 'string' && api.includes('equals/cards'),
    resolve: ({api, error, locale}) =>
      resolveEqualsCardsErrorMessage({api, error, locale}),
  },
  {
    match: (api) => typeof api === 'string' && api.startsWith('equals/'),
    resolve: ({api, error, locale}) =>
      resolveEqualsErrorMessage({endpoint: api, error, locale}),
  },
  {
    match: (api) => isCfsActionApi(api),
    resolve: ({api, error, locale}) =>
      resolveCfsActionErrorMessage({api, error, locale}),
  },
  {
    match: (api) => isCounterpartyApi(api),
    resolve: ({error, locale}) =>
      resolveCounterpartyErrorMessage({error, locale}),
  },
  {
    match: (api) => isPaywallApi(api),
    resolve: ({error, locale}) => resolvePaywallErrorMessage({error, locale}),
  },
  {
    match: (api) => isInvoiceApi(api),
    resolve: ({error, locale}) => resolveInvoiceErrorMessage({error, locale}),
  },
];

function normalizeLocale(locale) {
  return locale === 'en' ? 'en' : 'fr';
}

function getSupportId(error) {
  return (
    error?.error_id ||
    error?.errorId ||
    error?.trace_id ||
    error?.traceId ||
    error?.request_id ||
    error?.requestId ||
    null
  );
}

function appendSupportId(message, {supportId, locale}) {
  if (
    supportId == null ||
    supportId === '' ||
    supportId === 'null' ||
    supportId === 'undefined'
  )
    return message;
  const label = locale === 'en' ? 'Support ID' : 'ID support';
  return `${message} (${label}: ${supportId})`;
}

/**
 * Returns a localized message for an API error.
 *
 * - Uses a domain resolver if matched
 * - Otherwise falls back to common backend fields
 * - Finally falls back to a generic localized message
 */
export function resolveApiErrorMessage({api, error, locale} = {}) {
  const loc = normalizeLocale(locale);
  const supportId = getSupportId(error);

  for (const r of RESOLVERS) {
    if (r.match(api)) {
      const msg = r.resolve({api, error, locale: loc});
      if (msg) return appendSupportId(msg, {supportId, locale: loc});
    }
  }

  const fallback =
    error?.message ||
    error?.error_message ||
    error?.msg ||
    DEFAULT_ERROR_MESSAGES[loc];

  return appendSupportId(fallback, {supportId, locale: loc});
}

